import { useCallback, useState } from 'react'
import { ChatHero } from './components/ChatHero'
import { Composer } from './components/Composer'
import { Thread } from './components/Thread'
import { useRunQuery } from '@/lib/hooks/useRunQuery'
import { useSessionStore } from '@/stores/sessionStore'
import { useSettingsStore } from '@/stores/settingsStore'

/**
 * The chat view is a pure projection of the session store.
 *
 * History and Saved submit through the same `useRunQuery` hook and then
 * navigate here; because the thread and the in-flight request both live
 * outside this component, nothing has to be handed across the route boundary.
 *
 * An empty thread renders `ChatHero` — a centered landing screen with its own
 * embedded composer. The first exchange swaps that out for `Thread` plus the
 * composer docked at the bottom, which is where every exchange after it lives.
 */
export function ChatView() {
  const exchanges = useSessionStore((state) => state.exchanges)
  const autoScroll = useSettingsStore((state) => state.autoScroll)

  const { run, cancel, isRunning } = useRunQuery()

  const [draft, setDraft] = useState('')

  const isEmpty = exchanges.length === 0

  const handleSubmit = useCallback((query: string) => void run(query), [run])

  const handleRerun = useCallback(
    (query: string) => {
      setDraft('')
      void run(query)
    },
    [run],
  )

  if (isEmpty) {
    return (
      <ChatHero
        value={draft}
        onChange={setDraft}
        onSubmit={handleSubmit}
        onCancel={cancel}
        isRunning={isRunning}
      />
    )
  }

  return (
    <div className="flex h-full min-h-0">
      <div className="flex min-w-0 flex-1 flex-col">
        <Thread exchanges={exchanges} autoScroll={autoScroll} onRerun={handleRerun} />

        <Composer
          variant="dock"
          value={draft}
          onChange={setDraft}
          onSubmit={handleSubmit}
          onCancel={cancel}
          isRunning={isRunning}
        />
      </div>
    </div>
  )
}
